import { canonicalizeCategoryLabel } from '../../constants/marketplaceCategories';

export const MARKETPLACE_KEYWORD_MAX_LENGTH = 100;
export const MARKETPLACE_KEYWORD_MIN_LENGTH = 2;

export const MARKETPLACE_INFLUENCERS_PATH = '/marketplace/influencers';
export const MARKETPLACE_CAMPAIGNS_PATH = '/marketplace/campaigns';

/**
 * Trim, strip leading @/# (handles), and cap length.
 */
export const normalizeMarketplaceKeyword = (value) => {
  let next = String(value || '').trim();
  while (next.startsWith('@') || next.startsWith('#')) {
    next = next.slice(1).trim();
  }
  return next.slice(0, MARKETPLACE_KEYWORD_MAX_LENGTH);
};

export const readMarketplaceKeyword = (search) => {
  const params = new URLSearchParams(search || '');
  return normalizeMarketplaceKeyword(params.get('q') || params.get('search') || '');
};

export const getMarketplaceRequestKeyword = (value) => {
  const normalized = normalizeMarketplaceKeyword(value);
  return normalized.length >= MARKETPLACE_KEYWORD_MIN_LENGTH ? normalized : '';
};

export const setMarketplaceKeywordParam = (search, value) => {
  const params = new URLSearchParams(search || '');
  const requestKeyword = getMarketplaceRequestKeyword(value);
  params.delete('q');
  params.delete('search');
  if (requestKeyword) {
    params.set('q', requestKeyword);
  }
  const next = params.toString();
  return next ? `?${next}` : '';
};

/**
 * Location update for committing a keyword on the current marketplace list route.
 * Returns null when the search string would be unchanged.
 */
export const buildMarketplaceKeywordLocation = ({
  pathname,
  search,
  hash = '',
  value,
}) => {
  const nextSearch = setMarketplaceKeywordParam(search, value);
  if (nextSearch === search) return null;
  return {
    pathname,
    search: nextSearch,
    hash,
  };
};

/** Influencers search campaigns; brands and guests browse creators. */
export const getMarketplaceSearchPath = (userType) => (
  userType === 'influencer' ? MARKETPLACE_CAMPAIGNS_PATH : MARKETPLACE_INFLUENCERS_PATH
);

export const getMarketplaceSearchPlaceholder = () => 'Search Millet';

export const isMarketplaceListPath = (pathname) => (
  pathname === MARKETPLACE_INFLUENCERS_PATH || pathname === MARKETPLACE_CAMPAIGNS_PATH
);

/**
 * Build the location for a chrome search submit.
 * Preserves other filters only when already on the destination list route.
 */
export const buildMarketplaceSearchLocation = ({
  pathname,
  search,
  userType,
  keyword,
}) => {
  const targetPath = getMarketplaceSearchPath(userType);
  const baseSearch = pathname === targetPath ? search : '';
  return {
    pathname: targetPath,
    search: setMarketplaceKeywordParam(baseSearch, keyword),
  };
};

export const MARKETPLACE_CATEGORY_ALL = 'All';

export const readMarketplaceCategory = (search) => {
  const params = new URLSearchParams(search || '');
  const category = String(params.get('category') || '').trim();
  if (!category) return MARKETPLACE_CATEGORY_ALL;
  return canonicalizeCategoryLabel(category, category);
};

export const setMarketplaceCategoryParam = (search, category) => {
  const params = new URLSearchParams(search || '');
  params.delete('category');
  const normalized = String(category || '').trim();
  if (normalized && normalized !== MARKETPLACE_CATEGORY_ALL) {
    params.append('category', normalized);
  }
  const next = params.toString();
  return next ? `?${next}` : '';
};

/**
 * Build the location for a chrome category pick.
 * Preserves other filters only when already on the destination list route.
 * Optionally sets/clears q when keyword is provided (string, including '').
 */
export const buildMarketplaceCategoryLocation = ({
  pathname,
  search,
  userType,
  category,
  keyword,
}) => {
  const targetPath = getMarketplaceSearchPath(userType);
  const baseSearch = pathname === targetPath ? search : '';
  let nextSearch = setMarketplaceCategoryParam(baseSearch, category);
  if (keyword !== undefined) {
    nextSearch = setMarketplaceKeywordParam(nextSearch, keyword);
  }
  return {
    pathname: targetPath,
    search: nextSearch,
  };
};

export const MARKETPLACE_PLATFORM_PARAM = 'platform';
export const MARKETPLACE_CONTENT_TYPE_PARAM = 'content_type';
export const MARKETPLACE_CATEGORY_PARAM = 'category';
export const MARKETPLACE_SAVED_PARAM = 'saved';
export const MARKETPLACE_FOLLOWING_PARAM = 'following';
export const MARKETPLACE_FOLLOWING_VALUE = 'Following';
export const MARKETPLACE_MIN_RATING_PARAM = 'min_rating';
export const MARKETPLACE_MIN_FOLLOWERS_PARAM = 'min_followers';

const toParamValues = (values) => {
  if (values === undefined || values === null) return [];
  const list = Array.isArray(values) ? values : [values];
  return list
    .map((value) => String(value || '').trim())
    .filter((value) => value && value !== MARKETPLACE_CATEGORY_ALL);
};

/**
 * Replace a search param with zero or more values. Omits the key when empty.
 */
export const setMarketplaceSearchParam = (search, key, values) => {
  const params = new URLSearchParams(search || '');
  params.delete(key);
  toParamValues(values).forEach((value) => {
    params.append(key, value);
  });
  const next = params.toString();
  return next ? `?${next}` : '';
};

export const readMarketplaceSearchParamValues = (search, key) => {
  const params = new URLSearchParams(search || '');
  return params.getAll(key).map((value) => String(value || '').trim()).filter(Boolean);
};

export const readMarketplacePlatforms = (search) => (
  readMarketplaceSearchParamValues(search, MARKETPLACE_PLATFORM_PARAM)
);

export const readMarketplaceContentTypes = (search) => (
  readMarketplaceSearchParamValues(search, MARKETPLACE_CONTENT_TYPE_PARAM)
);

export const readMarketplaceSaved = (search) => {
  const value = String(new URLSearchParams(search || '').get(MARKETPLACE_SAVED_PARAM) || '')
    .trim()
    .toLowerCase();
  return value === '1' || value === 'true';
};

export const setMarketplaceSavedParam = (search, enabled) => {
  const params = new URLSearchParams(search || '');
  params.delete(MARKETPLACE_SAVED_PARAM);
  if (enabled) {
    params.set(MARKETPLACE_SAVED_PARAM, '1');
  }
  const next = params.toString();
  return next ? `?${next}` : '';
};

export const readMarketplaceFollowingBrands = (search) => (
  readMarketplaceSearchParamValues(search, MARKETPLACE_FOLLOWING_PARAM)
    .includes(MARKETPLACE_FOLLOWING_VALUE)
);

export const setMarketplaceFollowingBrandsParam = (search, enabled) => {
  const params = new URLSearchParams(search || '');
  const otherValues = params
    .getAll(MARKETPLACE_FOLLOWING_PARAM)
    .map((value) => String(value || '').trim())
    .filter((value) => value && value !== MARKETPLACE_FOLLOWING_VALUE);
  params.delete(MARKETPLACE_FOLLOWING_PARAM);
  if (enabled) {
    params.append(MARKETPLACE_FOLLOWING_PARAM, MARKETPLACE_FOLLOWING_VALUE);
  }
  otherValues.forEach((value) => {
    params.append(MARKETPLACE_FOLLOWING_PARAM, value);
  });
  const next = params.toString();
  return next ? `?${next}` : '';
};

/**
 * Clear chrome facet params (platform, category, content_type, saved, following).
 * Keeps q and other keys.
 */
export const clearMarketplaceFacetParams = (search) => {
  const params = new URLSearchParams(search || '');
  params.delete(MARKETPLACE_PLATFORM_PARAM);
  params.delete(MARKETPLACE_CATEGORY_PARAM);
  params.delete(MARKETPLACE_CONTENT_TYPE_PARAM);
  params.delete(MARKETPLACE_SAVED_PARAM);
  params.delete(MARKETPLACE_FOLLOWING_PARAM);
  params.delete(MARKETPLACE_MIN_RATING_PARAM);
  params.delete(MARKETPLACE_MIN_FOLLOWERS_PARAM);
  const next = params.toString();
  return next ? `?${next}` : '';
};

/**
 * Build a marketplace list location with optional facet + keyword updates.
 * Only provided facet keys are rewritten; omitted keys are left as-is on-path.
 * Off-path starts from a clean search string.
 */
export const buildMarketplaceFacetLocation = ({
  pathname,
  search,
  userType,
  keyword,
  category,
  platforms,
  contentTypes,
  saved,
  followingBrands,
  clearFacets = false,
}) => {
  const targetPath = getMarketplaceSearchPath(userType);
  let nextSearch = pathname === targetPath ? search : '';

  if (clearFacets) {
    nextSearch = clearMarketplaceFacetParams(nextSearch);
  }

  if (category !== undefined) {
    nextSearch = setMarketplaceCategoryParam(nextSearch, category);
  }
  if (platforms !== undefined) {
    nextSearch = setMarketplaceSearchParam(nextSearch, MARKETPLACE_PLATFORM_PARAM, platforms);
  }
  if (contentTypes !== undefined) {
    nextSearch = setMarketplaceSearchParam(
      nextSearch,
      MARKETPLACE_CONTENT_TYPE_PARAM,
      contentTypes,
    );
  }
  if (saved !== undefined) {
    nextSearch = setMarketplaceSavedParam(nextSearch, Boolean(saved));
  }
  if (followingBrands !== undefined) {
    nextSearch = setMarketplaceFollowingBrandsParam(nextSearch, Boolean(followingBrands));
  }
  if (keyword !== undefined) {
    nextSearch = setMarketplaceKeywordParam(nextSearch, keyword);
  }

  return {
    pathname: targetPath,
    search: nextSearch,
  };
};

const joinMarketplaceLocationHref = ({ pathname, search }) => (
  `${pathname || ''}${search || ''}`
);

/**
 * Build eBay-style breadcrumbs for marketplace list pages.
 * Always includes Marketplace › Campaigns / Influencers, then any active facets.
 *
 * Trail order: Marketplace > list > Platforms > value > Categories > value >
 * Format > value > Saved / Brands you follow.
 */
export const buildMarketplaceFilterBreadcrumbs = ({
  pathname,
  search,
  userType,
}) => {
  const targetPath = isMarketplaceListPath(pathname)
    ? pathname
    : getMarketplaceSearchPath(userType);
  const platforms = readMarketplacePlatforms(search);
  const category = readMarketplaceCategory(search);
  const contentTypes = readMarketplaceContentTypes(search);
  const saved = readMarketplaceSaved(search);
  const followingBrands = readMarketplaceFollowingBrands(search);
  const hasCategory = Boolean(category && category !== MARKETPLACE_CATEGORY_ALL);
  const hasPlatforms = platforms.length > 0;
  const hasFormat = contentTypes.length > 0;
  const listLabel = targetPath === MARKETPLACE_CAMPAIGNS_PATH ? 'Campaigns' : 'Influencers';

  const keyword = readMarketplaceKeyword(search);
  const locWith = (facets) => {
    const built = buildMarketplaceFacetLocation({
      pathname: targetPath,
      search: '',
      userType,
      keyword,
      category: facets.category === undefined ? MARKETPLACE_CATEGORY_ALL : facets.category,
      platforms: facets.platforms === undefined ? [] : facets.platforms,
      contentTypes: facets.contentTypes === undefined ? [] : facets.contentTypes,
      saved: Boolean(facets.saved),
      followingBrands: Boolean(facets.followingBrands),
    });
    return {
      pathname: built.pathname,
      search: built.search,
      href: joinMarketplaceLocationHref(built),
    };
  };

  const items = [{
    id: 'marketplace',
    label: 'Marketplace',
    to: locWith({}),
  }, {
    id: 'list',
    label: listLabel,
    to: locWith({}),
  }];

  let keptPlatforms = [];
  let keptCategory = MARKETPLACE_CATEGORY_ALL;
  let keptContentTypes = [];

  if (hasPlatforms) {
    items.push({
      id: 'platforms',
      label: 'Platforms',
      to: locWith({}),
    });
    keptPlatforms = platforms;
    items.push({
      id: 'platform-value',
      label: platforms.join(', '),
      to: locWith({ platforms: keptPlatforms }),
    });
  }

  if (hasCategory) {
    items.push({
      id: 'categories',
      label: 'Categories',
      to: locWith({ platforms: keptPlatforms }),
    });
    keptCategory = category;
    items.push({
      id: 'category-value',
      label: category,
      to: locWith({ platforms: keptPlatforms, category: keptCategory }),
    });
  }

  if (hasFormat) {
    items.push({
      id: 'format',
      label: 'Format',
      to: locWith({ platforms: keptPlatforms, category: keptCategory }),
    });
    keptContentTypes = contentTypes;
    items.push({
      id: 'format-value',
      label: contentTypes.join(', '),
      to: locWith({
        platforms: keptPlatforms,
        category: keptCategory,
        contentTypes: keptContentTypes,
      }),
    });
  }

  if (saved) {
    items.push({
      id: 'saved',
      label: 'Saved',
      to: locWith({
        platforms: keptPlatforms,
        category: keptCategory,
        contentTypes: keptContentTypes,
        saved: true,
      }),
    });
  }

  if (followingBrands) {
    const followingLabel = userType === 'influencer' ? 'Brands you follow' : 'Saved';
    items.push({
      id: 'following',
      label: followingLabel,
      to: locWith({
        platforms: keptPlatforms,
        category: keptCategory,
        contentTypes: keptContentTypes,
        saved,
        followingBrands: true,
      }),
    });
  }

  const lastIndex = items.length - 1;
  const normalized = items.map((item, index) => (
    index === lastIndex
      ? { id: item.id, label: item.label, to: null }
      : item
  ));

  return {
    items: normalized,
    title: normalized[lastIndex].label,
  };
};
